import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Box, Button, Grid, Skeleton, Stack, Typography } from '@mui/material';
import { FitnessCenter, Tune } from '@mui/icons-material';
import AppBarContext from 'context/AppBarContext';
import PremiumCard from 'components/shared/surfaces/PremiumCard';
import { premiumButtonSx } from 'common/theme/button/premiumButtonSx';
import PlayTodayRoutineCard from 'components/play/PlayTodayRoutineCard';
import PlayLastWorkoutTile from 'components/play/home/PlayLastWorkoutTile';
import AddToRoutineDialog from 'components/play/AddToRoutineDialog';
import PlayExerciseDetailModal from 'components/play/PlayExerciseDetailModal';
import PlayOnboardingWizard from 'components/play/onboarding/PlayOnboardingWizard';
import PlayWeekStrip from 'components/play/home/PlayWeekStrip';
import PlayMuscleMapCard from 'components/play/home/PlayMuscleMapCard';
import PlayExerciseDrawer from 'components/play/home/PlayExerciseDrawer';
import { MY_PLAY_PROFILE, MY_WEEKLY_ROUTINE } from 'api/graphql/play';

/**
 * OTRIX Play — home / hero.
 *
 * Orquesta la pantalla: la tira con los 7 días de la semana, el mapa muscular con la
 * distribución de la rutina, la card de hoy (motor de ejecución) y los resúmenes de rendimiento
 * y semana. Tocar un músculo abre los ejercicios en un drawer, así que la rutina nunca se pierde
 * de vista. Auto-abre el onboarding la primera vez y permite reconfigurarlo cuando se quiera.
 */
function OtrixPlayHome() {
  const { t } = useTranslation(['play']);
  const { setAppBarTitle } = useContext(AppBarContext);

  const platform = useSelector((state) => state.auth.user?.platform);
  const isOtrixPlay = platform === 'otrix_play';

  const [selectedMuscle, setSelectedMuscle] = useState(null);
  const [addTarget, setAddTarget] = useState(null);
  const [detailTarget, setDetailTarget] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);

  useEffect(() => {
    setAppBarTitle('OTRIX Play');
  }, [setAppBarTitle]);

  const { data, loading, error } = useQuery(MY_WEEKLY_ROUTINE, { fetchPolicy: 'cache-and-network' });
  const { data: profileData } = useQuery(MY_PLAY_PROFILE, { fetchPolicy: 'cache-and-network' });

  const profile = profileData?.myPlayProfile || null;
  const routineItems = useMemo(() => data?.myWeeklyRoutine?.items || [], [data]);
  const routineDays = useMemo(() => data?.myWeeklyRoutine?.days || [], [data]);

  // Auto-open the onboarding the first time (only OTRIX Play, only until completed).
  useEffect(() => {
    if (isOtrixPlay && profileData && !profile?.onboarding_completed_at) {
      setWizardOpen(true);
    }
  }, [isOtrixPlay, profileData, profile]);

  const hasRoutine = routineItems.length > 0;
  const firstLoad = loading && !data;

  const renderWeek = () => {
    if (firstLoad) {
      return (
        <Stack direction="row" spacing={1.5} sx={{ overflow: 'hidden' }}>
          {[0, 1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton
              key={i}
              variant="rounded"
              height={230}
              sx={{ flex: 1, minWidth: 140, borderRadius: '16px' }}
            />
          ))}
        </Stack>
      );
    }

    if (error) {
      // El detalle técnico va al log de Apollo; al usuario solo el mensaje.
      return (
        <PremiumCard>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
            {t('home.error', { ns: 'play' })}
          </Typography>
        </PremiumCard>
      );
    }

    if (!hasRoutine) {
      return (
        <PremiumCard>
          <Stack spacing={1.5} alignItems="center" sx={{ py: 4, textAlign: 'center' }}>
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: '50%',
                flex: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'gold.main',
                border: '1px solid',
                borderColor: 'hairline.main',
                backgroundColor: 'surface.overlay'
              }}>
              <FitnessCenter />
            </Box>
            <Typography variant="h6" fontWeight={800}>
              {t('home.emptyTitle', { ns: 'play' })}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
              {t('home.emptyBody', { ns: 'play' })}
            </Typography>
            <Button onClick={() => setWizardOpen(true)} sx={{ ...premiumButtonSx, mt: 1 }}>
              {t('home.emptyCta', { ns: 'play' })}
            </Button>
          </Stack>
        </PremiumCard>
      );
    }

    return <PlayWeekStrip items={routineItems} days={routineDays} />;
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Hero */}
      <PremiumCard sx={{ mb: 3 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between">
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 52,
                height: 52,
                flex: 'none',
                borderRadius: '14px',
                display: { xs: 'none', sm: 'flex' },
                alignItems: 'center',
                justifyContent: 'center',
                color: 'gold.main',
                border: '1px solid',
                borderColor: 'hairline.main',
                backgroundColor: 'surface.overlay'
              }}>
              <FitnessCenter />
            </Box>
            {/* El h4 no escalaba: a 320 el título se salía de la card. */}
            <Stack spacing={0.5} sx={{ minWidth: 0 }}>
              <Typography
                variant="h4"
                fontWeight={800}
                letterSpacing="-0.02em"
                sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                {t('home.title', { ns: 'play' })}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('home.subtitle', { ns: 'play' })}
              </Typography>
            </Stack>
          </Stack>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<Tune />}
            onClick={() => setWizardOpen(true)}
            sx={{
              flex: 'none',
              borderColor: 'hairline.strong',
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: 2.5,
              alignSelf: { xs: 'stretch', sm: 'center' }
            }}>
            {t('home.reconfigure', { ns: 'play' })}
          </Button>
        </Stack>
      </PremiumCard>

      {/* Semana */}
      <Box sx={{ mb: 3 }}>{renderWeek()}</Box>

      {/* Mapa · hoy · rendimiento. "Tu semana" se eliminó: era redundante con la tira de
          días de arriba; la racha vive ahora en el tile de rendimiento y en Rendimientos. */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6} lg={4}>
          <PlayMuscleMapCard items={routineItems} onSelect={setSelectedMuscle} />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <PlayTodayRoutineCard compact />
        </Grid>
        <Grid item xs={12} lg={4}>
          <PlayLastWorkoutTile />
        </Grid>
      </Grid>

      <Stack spacing={0.5} sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 700 }}>
          {t('home.footerTitle', { ns: 'play' })}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {t('home.footerBody', { ns: 'play' })}
        </Typography>
      </Stack>

      <PlayExerciseDrawer
        muscle={selectedMuscle}
        open={Boolean(selectedMuscle)}
        onClose={() => setSelectedMuscle(null)}
        onAdd={setAddTarget}
        onOpenDetail={setDetailTarget}
      />
      <AddToRoutineDialog open={Boolean(addTarget)} exercise={addTarget} onClose={() => setAddTarget(null)} />
      <PlayExerciseDetailModal
        exercise={detailTarget}
        open={Boolean(detailTarget)}
        onClose={() => setDetailTarget(null)}
        onAdd={setAddTarget}
      />
      <PlayOnboardingWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        initialProfile={profile?.onboarding_completed_at ? profile : null}
      />
    </Box>
  );
}

export default OtrixPlayHome;
